import { cn } from '@/lib/cn';

/**
 * PARACON wordmark.
 *
 * `src` is the company's own logo, uploaded in Company settings — the same
 * image that already prints on sign-on posters and PDF exports. Passing it here
 * means the client changes their mark by uploading a file rather than waiting
 * for a release, which is how they asked for everything else about their own
 * content to work.
 *
 * Without one, the packaged mark: the real art-deco lettering, lifted from the
 * client's own tender letter template, in white for navy surfaces and ink for
 * light ones. It was a tracked-out approximation in a display font until the
 * artwork arrived.
 */
/** The packaged artwork, from the client's own letterhead. */
const DARK_MARK = '/brand/paracon-wordmark.png';
const LIGHT_MARK = '/brand/paracon-wordmark-light.png';

export function ParaconLogo({
  className,
  tone = 'light',
  src,
}: {
  className?: string;
  /** `light` = white lettering for navy surfaces. `dark` = ink on white. */
  tone?: 'light' | 'dark';
  /** The company's uploaded logo, when it has one. */
  src?: string | null;
}) {
  /*
    On a navy surface the packaged white mark wins, whatever has been uploaded.
    A company logo is made for paper: Paracon's is a JPEG, which cannot hold
    transparency, so it arrived as the wordmark on a white rectangle stuck to
    the middle of the sidebar. The uploaded file still prints on everything
    that ends up on paper, where it belongs.
  */
  const source = tone === 'light' ? LIGHT_MARK : src || DARK_MARK;

  return (
    <img
      src={source}
      alt="Paracon"
      // Contained rather than cropped: a logo is whatever shape it is, and the
      // height is what keeps the header bar even.
      className={cn('h-7 w-auto max-w-[160px] object-contain object-left', className)}
    />
  );
}

/**
 * Square mark for the collapsed rail and the favicon shape.
 *
 * Always the packaged letter: the wordmark is far too wide to read at 32px,
 * and an uploaded logo is made for paper — Paracon's is a JPEG, which cannot
 * hold transparency and arrives as a white rectangle on a navy rail.
 */
export function ParaconMark({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 font-display text-base font-semibold text-white',
        className,
      )}
    >
      P
    </span>
  );
}
